import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9090';
const WHATSAPP_API_BASE_URL_VALUE = process.env.REACT_APP_WHATSAPP_API_BASE_URL || 'https://whatsapp.snwolley.ai';

// Token management
const TOKEN_KEY = 'snwolley_auth_token';

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export { API_BASE_URL };
const setAuthToken = (token) => localStorage.setItem(TOKEN_KEY, token);
const removeAuthToken = () => localStorage.removeItem(TOKEN_KEY);

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.response?.data);
    
    // Handle common errors
    if (error.response?.status === 401) {
      console.error('Unauthorized access - please log in again');
    } else if (error.response?.status === 403) {
      const errorData = error.response?.data;
      if (errorData?.missing_permission) {
        console.error(`Permission denied: Missing ${errorData.missing_permission}`, errorData.message);
        // Attach user-friendly message for UI
        error.userMessage = errorData.message || 'You do not have permission to access this feature.';
        error.suggestedAction = errorData.suggested_action || 'Contact your administrator for access.';
      } else {
        console.error('Access denied - insufficient permissions');
        error.userMessage = 'You do not have permission to access this feature.';
      }
    } else if (error.response?.status === 500) {
      console.error('Server error - check backend services');
      error.userMessage = 'A server error occurred. Please try again or contact support.';
    } else if (!error.response) {
      console.error('Network error - check connectivity');
      error.userMessage = 'Network error. Please check your connection and try again.';
    }
    
    return Promise.reject(error);
  }
);

// Helper function to get user-friendly error message
export const getErrorMessage = (error) => {
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.response?.status === 403) {
    return 'You do not have permission to access this feature. Contact your administrator.';
  }
  
  if (error.response?.status === 401) {
    return 'Please log in to access this feature.';
  }
  
  if (error.response?.status === 500) {
    return 'A server error occurred. Please try again later.';
  }
  
  if (!error.response) {
    return 'Network error. Please check your connection.';
  }
  
  return error.response?.data?.message || 'An error occurred. Please try again.';
};

// Helper function to check if error is due to missing permissions
export const isPermissionError = (error) => {
  return error.response?.status === 403;
};

export const WHATSAPP_API_BASE_URL = WHATSAPP_API_BASE_URL_VALUE;

export const api = {
  // Client API endpoints (via frontend proxy)
  async getClients() {
    return apiClient.get('/api/clients');
  },

  // Get WhatsApp clients - using existing clients endpoint
  // This assumes the /api/clients endpoint returns WhatsApp client data
  async getWhatsAppClients() {
    return apiClient.get('/api/clients');
  },

  async updateClient(clientId, updateData) {
    return apiClient.put(`/api/clients/${clientId}`, updateData);
  },

  async createClient(clientData) {
    return apiClient.post('/api/clients/register', clientData);
  },

  async deleteClient(clientId) {
    return apiClient.delete(`/api/clients/${clientId}`);
  },

  async getClientStatus(clientId) {
    return apiClient.get(`/api/clients/${clientId}/status`);
  },

  async createWhatsAppSession(clientId, sessionData = {}) {
    return apiClient.post(`/api/clients/${clientId}/session`, sessionData);
  },

  // Container Manager API (via frontend proxy)
  async getContainerManagerHealth() {
    return apiClient.get('/api/container/health');
  },

  async reloadNginx() {
    return apiClient.post('/api/container/nginx/reload');
  },

  // Reprovision a client's container (destroys + recreates container/session).
  // Same endpoint as reprovision_prod.sh.
  async reprovisionClient(clientId) {
    return apiClient.post(`/api/container/clients/${clientId}/reprovision`, {}, {
      // Reprovision recreates the container + session; give it room to finish.
      timeout: 180000
    });
  },

  // Webhook Service API (via frontend proxy)
  async getWebhookStats() {
    return apiClient.get('/api/webhook/stats');
  },

  // Dashboard stats API
  async getDashboardStats() {
    return apiClient.get('/api/dashboard/stats');
  },

  async getValidKeys() {
    return apiClient.get('/api/webhook/valid-keys');
  },

  async testWebhookAuth(apiKey) {
    return apiClient.post('/api/webhook/test-auth', {}, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  // WhatsApp Instance API (direct to container)
  async getWahaSessionInfo(port, sessionName, apiKey = 'AlphanumericAndSpace') {
    // Use proxy route through frontend nginx
    return apiClient.get(`/api/snwolley/${port}/sessions/${sessionName}`, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  async getWahaQRCode(port, sessionName, apiKey = 'AlphanumericAndSpace') {
    return apiClient.get(`/api/snwolley/${port}/screenshot?session=${sessionName}`, {
      headers: { 
        'x-api-key': apiKey,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
  },

  async requestPairingCode(port, sessionName, apiKey, phoneNumber) {
    return apiClient.post(`/api/snwolley/${port}/${sessionName}/auth/request-code`, {
      phoneNumber: phoneNumber
    }, {
      headers: { 
        'X-API-Key': apiKey,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
  },

  async getWahaSessions(port, apiKey = 'AlphanumericAndSpace') {
    return apiClient.get(`/api/snwolley/${port}/sessions`, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  async startWahaSession(port, sessionName, apiKey = 'AlphanumericAndSpace') {
    return apiClient.post(`/api/snwolley/${port}/sessions/${sessionName}/start`, {}, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  async stopWahaSession(port, sessionName, apiKey = 'AlphanumericAndSpace') {
    return apiClient.post(`/api/snwolley/${port}/sessions/${sessionName}/stop`, {}, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  async restartWahaSession(port, sessionName, apiKey = 'AlphanumericAndSpace') {
    return apiClient.post(`/api/snwolley/${port}/sessions/${sessionName}/restart`, {}, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  async logoutWahaSession(port, sessionName, apiKey = 'AlphanumericAndSpace') {
    return apiClient.post(`/api/snwolley/${port}/sessions/${sessionName}/logout`, {}, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  async sendTestMessage(port, sessionName, chatId, message, apiKey = 'AlphanumericAndSpace') {
    return apiClient.post(`/api/snwolley/${port}/sendText`, {
      session: sessionName,
      chatId: chatId,
      text: message
    }, {
      headers: { 'X-API-Key': apiKey }
    });
  },

  // New backend messaging endpoints with automatic API key handling
  async sendTextMessage(clientId, chatId, text) {
    return apiClient.post('/api/send/text', {
      client_id: clientId,
      chat_id: chatId,
      text: text
    });
  },

  async sendImageMessage(clientId, chatId, imageUrl, caption = '') {
    return apiClient.post('/api/send/image', {
      client_id: clientId,
      chat_id: chatId,
      image: imageUrl, // Changed from 'file' to 'image'
      caption: caption
    });
  },

  async sendBulkMessage(campaignData) {
    return apiClient.post('/api/send/bulk', campaignData);
  },

  async sendVoiceMessage(clientId, chatId, voiceUrl) {
    return apiClient.post('/api/send/voice', {
      client_id: clientId,
      chat_id: chatId,
      voice: voiceUrl  // Changed from 'file' to 'voice' to match image pattern
    });
  },

  async sendLocationMessage(clientId, chatId, latitude, longitude, title = '') {
    return apiClient.post('/api/send/location', {
      client_id: clientId,
      chat_id: chatId,
      latitude: latitude,
      longitude: longitude,
      title: title
    });
  },

  async sendPollMessage(clientId, chatId, pollData) {
    return apiClient.post('/api/send/poll', {
      client_id: clientId,
      chat_id: chatId,
      poll: pollData
    });
  },

  async sendFileMessage(clientId, chatId, fileData, caption = '') {
    return apiClient.post('/api/send/file', {
      client_id: clientId,
      chat_id: chatId,
      file: fileData,
      caption: caption
    });
  },

  async createChannel(clientId, channelData) {
    return apiClient.post(`/api/send/channels`, {
      client_id: clientId,
      channel: channelData
    });
  },

  // Roles and Permissions APIs
  async getRoles() {
    return apiClient.get('/api/roles');
  },

  async getRolesWithPermissions() {
    return apiClient.get('/api/roles-with-permissions');
  },

  async getPermissions() {
    return apiClient.get('/api/permissions');
  },

  // Organization User Management APIs
  async getOrganizationUsers() {
    return apiClient.get('/api/organization/users');
  },

  async createOrganizationUser(userData) {
    return apiClient.post('/api/organization/users', userData);
  },

  async updateOrganizationUser(userId, userData) {
    return apiClient.put(`/api/organization/users/${userId}`, userData);
  },

  async deleteOrganizationUser(userId) {
    return apiClient.delete(`/api/organization/users/${userId}`);
  },

  // Permission Management APIs (using organization routes)
  async getUserPermissions(userId) {
    return apiClient.get(`/api/organization/users/${userId}/permissions`);
  },

  async assignUserRole(userId, roleData) {
    return apiClient.post(`/api/organization/users/${userId}/roles`, roleData);
  },

  async removeUserRole(userId, roleName) {
    return apiClient.delete(`/api/organization/users/${userId}/roles/${roleName}`);
  },

  async grantUserPermission(userId, permissionData) {
    return apiClient.post(`/api/organization/users/${userId}/permissions`, permissionData);
  },

  async revokeUserPermission(userId, permissionName) {
    return apiClient.delete(`/api/organization/users/${userId}/permissions/${permissionName}`);
  },

  async bulkUpdateUserPermissions(userId, permissionUpdates) {
    return apiClient.post(`/api/organization/users/${userId}/permissions/bulk`, permissionUpdates);
  },

  // Authentication APIs
  async register(userData) {
    const response = await apiClient.post('/api/auth/register', userData);
    // Magic link registration doesn't return token immediately
    return response;
  },

  async sendMagicLink(email) {
    const response = await apiClient.post('/api/auth/send-magic-link', { email });
    return response;
  },

  // OTP Authentication
  async sendOTP(email, purpose = 'login') {
    const response = await apiClient.post('/api/auth/send-otp', { email, purpose });
    return response;
  },

  async verifyOTP(email, otp, purpose = 'login') {
    const response = await apiClient.post('/api/auth/verify-otp', { email, otp, purpose });
    if (response.data.token) {
      setAuthToken(response.data.token);
    }
    return response;
  },

  async resendOTP(email, purpose = 'login') {
    const response = await apiClient.post('/api/auth/resend-otp', { email, purpose });
    return response;
  },

  async verifyMagicLink(token) {
    const response = await apiClient.post('/api/auth/verify-magic-link', { token });
    if (response.data.token) {
      setAuthToken(response.data.token);
    }
    return response;
  },

  async login(credentials) {
    // For backward compatibility, redirect to magic link
    return this.sendMagicLink(credentials.email);
  },

  async logout() {
    removeAuthToken();
    // You might want to call an API endpoint to invalidate the token on the server
    return Promise.resolve();
  },

  async verifyToken() {
    return apiClient.get('/api/auth/verify');
  },

  async getCurrentUser() {
    return apiClient.get('/api/auth/verify');
  },

  async getUserProfile() {
    const response = await apiClient.get('/api/auth/profile');
    return response;
  },

  async updateUserProfile(profileData) {
    const response = await apiClient.put('/api/auth/profile', profileData);
    return response.data;
  },

  // Organization APIs
  async getOrganizationApiKeys(orgId) {
    return apiClient.get(`/api/organizations/${orgId}/api-keys`);
  },

  async getVerificationStatus() {
    const response = await apiClient.get('/api/organizations/verification-status');
    return response;
  },

  async getVerificationDocuments() {
    const response = await apiClient.get('/api/organizations/verification-documents');
    return response;
  },

  async getDocumentLimits(organizationId) {
    return apiClient.get(`/api/organizations/${organizationId}/document-limits`);
  },

  async submitVerificationDocuments(formData) {
    const response = await apiClient.post('/api/organizations/verification', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response;
  },

  // AI Agents API
  async getAgents() {
    return apiClient.get('/api/agents');
  },

  async createAgent(agentData) {
    return apiClient.post('/api/agents', agentData);
  },

  async verifyWebsite(url) {
    return apiClient.post('/api/agents/verify-website', { url });
  },

  async getAgent(agentId) {
    return apiClient.get(`/api/agents/${agentId}`);
  },

  async updateAgent(agentId, agentData) {
    return apiClient.put(`/api/agents/${agentId}`, agentData);
  },

  async deleteAgent(agentId) {
    return apiClient.delete(`/api/agents/${agentId}`);
  },
  
  // Agent auto-reply control
  async disableAgentAutoReply(agentId) {
    return apiClient.patch(`/api/agents/${agentId}/auto-reply`, {
      auto_reply_enabled: false
    });
  },

  async enableAgentAutoReply(agentId) {
    return apiClient.patch(`/api/agents/${agentId}/auto-reply`, {
      auto_reply_enabled: true
    });
  },


  // Agent Knowledge Base Documents
  async uploadAgentDocuments(agentId, formData) {
    return apiClient.post(`/api/agents/${agentId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  async getAgentDocuments(agentId) {
    return apiClient.get(`/api/agents/${agentId}/documents`);
  },

  async deleteAgentDocument(agentId, documentId) {
    return apiClient.delete(`/api/agents/${agentId}/documents/${documentId}`);
  },

  // Admin API
  async adminLogin(credentials) {
    return axios.post(`${API_BASE_URL}/api/admin/auth/login`, credentials);
  },

  async verifyAdminToken(token) {
    return axios.get(`${API_BASE_URL}/api/admin/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAdminDashboardStats(token) {
    return axios.get(`${API_BASE_URL}/api/admin/dashboard/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAllOrganizations(token, params = {}) {
    return axios.get(`${API_BASE_URL}/api/admin/organizations`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });
  },

  async exportOrganizations(token, filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.format) params.append('format', filters.format);
    
    return axios.get(`${API_BASE_URL}/api/admin/organizations/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob' // Important for file downloads
    });
  },

  async verifyOrganization(token, orgId, data) {
    return axios.post(`${API_BASE_URL}/api/admin/organizations/${orgId}/verify`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAllAdmins(token) {
    return axios.get(`${API_BASE_URL}/api/admin/admins`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async createAdmin(token, adminData) {
    return axios.post(`${API_BASE_URL}/api/admin/admins`, adminData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async updateAdmin(token, adminId, adminData) {
    return axios.put(`${API_BASE_URL}/api/admin/admins/${adminId}`, adminData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async deleteAdmin(token, adminId) {
    return axios.delete(`${API_BASE_URL}/api/admin/admins/${adminId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getOrganizationDetails(token, orgId) {
    return axios.get(`${API_BASE_URL}/api/admin/organizations/${orgId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getOrganizationAgents(token, orgId) {
    return axios.get(`${API_BASE_URL}/api/admin/organizations/${orgId}/agents`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAgentSessions(token, agentId) {
    return axios.get(`${API_BASE_URL}/api/admin/agents/${agentId}/sessions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getSessionMessages(token, sessionId, page = 1, limit = 50) {
    return axios.get(`${API_BASE_URL}/api/admin/sessions/${sessionId}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { page, limit }
    });
  },

  async getAllUsers(token) {
    return axios.get(`${API_BASE_URL}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async exportUsers(token, filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.organization) params.append('organization', filters.organization);
    if (filters.format) params.append('format', filters.format);
    
    return axios.get(`${API_BASE_URL}/api/admin/users/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob' // Important for file downloads
    });
  },

  async getAllWhatsAppAgents(token) {
    return axios.get(`${API_BASE_URL}/api/admin/whatsapp-agents`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAgentDetails(token, agentId) {
    return axios.get(`${API_BASE_URL}/api/admin/whatsapp-agents/${agentId}/details`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Auth utilities
  isAuthenticated() {
    return !!getAuthToken();
  },

  getToken() {
    return getAuthToken();
  },

  // Packages
  async getPackages() {
    return axios.get(`${API_BASE_URL}/api/packages`);
  },

  async getPackageLimits(organizationId) {
    return apiClient.get(`/api/organizations/${organizationId}/package-limits`);
  },

  // Admin System Settings
  async getAdminSystemSettings(token) {
    return axios.get(`${API_BASE_URL}/api/admin/settings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async updateSystemSetting(token, key, value) {
    return axios.put(`${API_BASE_URL}/api/admin/settings/${key}`, { value }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Organization Documents
  async getOrganizationDocuments(token, orgId) {
    return axios.get(`${API_BASE_URL}/api/admin/organizations/${orgId}/documents`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Subscription & Billing API
  async getSubscriptionStatus() {
    return apiClient.get('/api/dashboard/subscription-widget');
  },

  async getUsageStatistics(timeframe = '30d') {
    return apiClient.get(`/api/dashboard/usage-stats?timeframe=${timeframe}`);
  },

  async getPackagesWithPricing() {
    return apiClient.get('/api/payment/packages');
  },

  async upgradeSubscription(packageId, paymentMethod) {
    return apiClient.post('/api/payment/upgrade', {
      package_id: packageId,
      payment_method: paymentMethod
    });
  },

  async processPayment(paymentData) {
    return apiClient.post('/api/payment/process', paymentData);
  },

  // Mobile money payment methods
  async initiateNpontuPayment(packageId, phoneNumber, billingCycle = 'monthly') {
    return apiClient.post('/api/payment/initiate', {
      package_id: packageId,
      phone_number: phoneNumber,
      billing_cycle: billingCycle,
      payment_processor: 'mobile_money'
    });
  },

  async getPaymentStatus(paymentIntentId) {
    return apiClient.get(`/api/payment/status/${paymentIntentId}`);
  },

  async getBillingHistory() {
    return apiClient.get('/api/payment/history');
  },

  // System settings
  async getSystemSettings() {
    return apiClient.get('/api/system/settings');
  },

  async requestTrialExtension(reason) {
    return apiClient.post('/api/dashboard/trial-extend', { reason });
  },

  // Audit Logs API
  async getAuditLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/api/audit-logs?${queryString}` : '/api/audit-logs';
    return apiClient.get(url);
  },

  async getAuditLogsSummary() {
    return apiClient.get('/api/audit-logs/summary');
  },

  // Admin Audit Logs API
  async getAdminAuditLogs(token, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/api/admin/audit-logs?${queryString}` : '/api/admin/audit-logs';
    return axios.get(`${API_BASE_URL}${url}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAdminAuditLogsSummary(token) {
    return axios.get(`${API_BASE_URL}/api/admin/audit-logs/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAdminRoles(token) {
    return axios.get(`${API_BASE_URL}/api/admin/roles`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAdminRolesWithPermissions(token) {
    return axios.get(`${API_BASE_URL}/api/admin/roles-with-permissions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Admin Permission Management APIs
  async getAdminUserPermissions(userId, token) {
    return axios.get(`${API_BASE_URL}/api/admin/users/${userId}/permissions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async assignAdminUserRole(userId, roleData, token) {
    return axios.post(`${API_BASE_URL}/api/admin/users/${userId}/assign-role`, roleData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async removeAdminUserRole(userId, roleName, token) {
    return axios.delete(`${API_BASE_URL}/api/admin/users/${userId}/remove-role/${roleName}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async grantAdminUserPermission(userId, permissionData, token) {
    return axios.post(`${API_BASE_URL}/api/admin/users/${userId}/permissions`, permissionData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async revokeAdminUserPermission(userId, permissionName, token) {
    return axios.delete(`${API_BASE_URL}/api/admin/users/${userId}/permissions/${permissionName}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  async getAdminPermissions(token) {
    return axios.get(`${API_BASE_URL}/api/admin/permissions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Custom Tools APIs
  async getTools() {
    return apiClient.get('/api/agents/tools');
  },

  async createTool(toolData) {
    return apiClient.post('/api/agents/tools', toolData);
  },

  async updateTool(toolId, toolData) {
    return apiClient.put(`/api/agents/tools/${toolId}`, toolData);
  },

  async deleteTool(toolId) {
    return apiClient.delete(`/api/agents/tools/${toolId}`);
  },

  async getTool(toolId) {
    return apiClient.get(`/api/agents/tools/${toolId}`);
  },

  async testTool(toolId, parameters) {
    return apiClient.post(`/api/agents/tools/${toolId}/test`, { parameters });
  },

  // Client Session Management  
  async createClientSession(clientId, sessionData = {}) {
    return apiClient.post(`/api/clients/${clientId}/session`, sessionData);
  },

  // External API methods with API key authentication for testing
  async testExternalTextMessage(apiKey, chatId, text) {
    return axios.post(`${API_BASE_URL}/api/v1/send/text`, {
      chat_id: chatId,
      text: text
    }, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  async testExternalVoiceMessage(apiKey, chatId, fileData) {
    return axios.post(`${API_BASE_URL}/api/v1/send/voice`, {
      chat_id: chatId,
      file: fileData
    }, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  async testExternalLocationMessage(apiKey, chatId, latitude, longitude, title) {
    return axios.post(`${API_BASE_URL}/api/v1/send/location`, {
      chat_id: chatId,
      latitude: latitude,
      longitude: longitude,
      title: title
    }, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  async testExternalPollMessage(apiKey, chatId, pollData) {
    return axios.post(`${API_BASE_URL}/api/v1/send/poll`, {
      chat_id: chatId,
      poll: pollData
    }, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  async testExternalFileMessage(apiKey, chatId, fileData, caption) {
    return axios.post(`${API_BASE_URL}/api/v1/send/file`, {
      chat_id: chatId,
      file: fileData,
      caption: caption
    }, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  async testExternalClientInfo(apiKey) {
    return axios.get(`${API_BASE_URL}/api/v1/client/info`, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  },

  async testExternalGroups(apiKey, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/api/v1/send/groups${queryString ? `?${queryString}` : ''}`;
    
    return axios.get(url, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
  }
};

export default api;
